import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { setupWorker } from 'msw';

import { DataContextProvider } from './store/dataContext';
import { UiContextProvider } from './store/uiContext';
import { setupStore } from './commons/redux/store';
import { shouldAbTestRedirect } from './commons/utils/ab-testing';
import {
  platformCartIdValue,
  invoiceId,
  shouldLoadPaymentButtons,
  apiMethod,
  storeUrlValue,
  merchantIdValue,
} from './helpers/prelaunchData';
import { CHECKOUT_API_URL } from './helpers/urlConst';
import { handleInvoiceId, handleApiMethod } from './commons/utils/cross-origin-communication';
import App from './App';
import PaymentButtonsApp from './commons/components/payment-buttons-app';
import './helpers/i18n';
import { handlers } from './mocks/handlers';

class AppInitializer {
  constructor() {
    this.LDProvider = null;
    this.shouldRedirect = null;
  }

  async initialize() {
    if (process.env.REACT_APP_NODE_ENV === 'msw') {
      const msw = setupWorker(...handlers);
      msw.start();
    }

    if (shouldLoadPaymentButtons) {
      this.renderPaymentButtonsApp();
    } else if (invoiceId) {
      handleInvoiceId({ CHECKOUT_API_URL, merchantIdValue, platformCartIdValue, invoiceId });
    } else if (apiMethod) {
      handleApiMethod({
        apiMethod,
        CHECKOUT_API_URL,
        merchantIdValue,
        platformCartIdValue,
      });
    } else {
      await this.initializeLaunchDarkly();
      await this.handleAbTesting();
      this.renderCheckoutApp();
    }
  }

  async initializeLaunchDarkly() {
    this.LDProvider = await asyncWithLDProvider({
      clientSideID: process.env.REACT_APP_FEATURE_TOGGLE_CLIENT_ID,
      user: {
        name: storeUrlValue,
        key: merchantIdValue,
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
    });
  }

  async handleAbTesting() {
    this.shouldRedirect = await shouldAbTestRedirect();
  }

  renderCheckoutApp() {
    if (typeof this.shouldRedirect === 'boolean') {
      if (this.shouldRedirect) {
        window.parent.postMessage('REDIRECT_AB_TEST', '*');
      } else {
        this.renderUi();
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  renderPaymentButtonsApp() {
    document.querySelector('#payment-buttons-app').setAttribute('style', '');

    ReactDOM.render(
      <PaymentButtonsApp section={shouldLoadPaymentButtons} />,
      document.querySelector('#payment-buttons-app'),
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderUi() {
    document.querySelector('#checkout-app').setAttribute('style', 'align-items:center;height:100vh;');
    window.parent.postMessage('LOAD_IFRAME_CONTENTS', '*');

    ReactDOM.render(
      <UiContextProvider>
        <DataContextProvider>
          <this.LDProvider>
            <Provider store={setupStore()}>
              <App />
            </Provider>
          </this.LDProvider>
        </DataContextProvider>
      </UiContextProvider>,
      document.querySelector('#checkout-app'),
    );
  }
}

const appInitializer = new AppInitializer();
appInitializer.initialize();
